// Code Snippets Library - Quick Insert Common Patterns

export const snippets = {
  react: [
    {
      id: 'rfc',
      name: 'React Functional Component',
      description: 'Create a functional component',
      language: 'javascript',
      code: `import React from 'react';

const ComponentName = () => {
  return (
    <div>

    </div>
  );
};

export default ComponentName;`
    },
    {
      id: 'useState',
      name: 'useState Hook',
      description: 'React useState hook',
      language: 'javascript',
      code: `const [state, setState] = useState(initialValue);`
    },
    {
      id: 'useEffect',
      name: 'useEffect Hook',
      description: 'React useEffect hook',
      language: 'javascript',
      code: `useEffect(() => {
  // Effect logic here

  return () => {
    // Cleanup logic here
  };
}, [dependencies]);`
    },
    {
      id: 'useCallback',
      name: 'useCallback Hook',
      description: 'React useCallback hook',
      language: 'javascript',
      code: `const memoizedCallback = useCallback(
  () => {
    // Callback logic here
  },
  [dependencies]
);`
    },
    {
      id: 'useMemo',
      name: 'useMemo Hook',
      description: 'React useMemo hook',
      language: 'javascript',
      code: `const memoizedValue = useMemo(
  () => computeExpensiveValue(a, b),
  [a, b]
);`
    },
    {
      id: 'useContext',
      name: 'useContext Hook',
      description: 'React useContext hook',
      language: 'javascript',
      code: `import { createContext, useContext } from 'react';

const MyContext = createContext(defaultValue);

const value = useContext(MyContext);`
    },
    {
      id: 'custom-hook',
      name: 'Custom Hook',
      description: 'Custom React hook template',
      language: 'javascript',
      code: `import { useState, useEffect } from 'react';

const useCustomHook = (initialValue) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    // Hook logic here
  }, [value]);

  return [value, setValue];
};

export default useCustomHook;`
    }
  ],

  api: [
    {
      id: 'fetch-get',
      name: 'Fetch GET Request',
      description: 'Fetch data from API',
      language: 'javascript',
      code: `const fetchData = async () => {
  try {
    const response = await fetch('https://api.example.com/data');
    if (!response.ok) throw new Error('Failed to fetch');
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error:', error);
    throw error;
  }
};`
    },
    {
      id: 'fetch-post',
      name: 'Fetch POST Request',
      description: 'POST data to API',
      language: 'javascript',
      code: `const postData = async (data) => {
  try {
    const response = await fetch('https://api.example.com/data', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to post');
    return await response.json();
  } catch (error) {
    console.error('Error:', error);
    throw error;
  }
};`
    },
    {
      id: 'axios-get',
      name: 'Axios GET Request',
      description: 'Fetch data with Axios',
      language: 'javascript',
      code: `import axios from 'axios';

const fetchData = async () => {
  try {
    const response = await axios.get('https://api.example.com/data');
    return response.data;
  } catch (error) {
    console.error('Error:', error);
    throw error;
  }
};`
    },
    {
      id: 'axios-post',
      name: 'Axios POST Request',
      description: 'POST data with Axios',
      language: 'javascript',
      code: `import axios from 'axios';

const postData = async (data) => {
  try {
    const response = await axios.post('https://api.example.com/data', data);
    return response.data;
  } catch (error) {
    console.error('Error:', error);
    throw error;
  }
};`
    }
  ],

  express: [
    {
      id: 'express-route',
      name: 'Express Route',
      description: 'Basic Express route',
      language: 'javascript',
      code: `app.get('/api/endpoint', async (req, res) => {
  try {
    // Route logic here
    res.json({ success: true, data: {} });
  } catch (error) {
    res.status(500).json({ error: error.message });
  }
});`
    },
    {
      id: 'express-middleware',
      name: 'Express Middleware',
      description: 'Custom middleware',
      language: 'javascript',
      code: `const customMiddleware = (req, res, next) => {
  // Middleware logic here
  next();
};

app.use(customMiddleware);`
    },
    {
      id: 'express-error-handler',
      name: 'Express Error Handler',
      description: 'Error handling middleware',
      language: 'javascript',
      code: `app.use((err, req, res, next) => {
  console.error(err.stack);
  res.status(500).json({
    error: 'Something went wrong!',
    message: err.message
  });
});`
    }
  ],

  fastapi: [
    {
      id: 'fastapi-route',
      name: 'FastAPI Route',
      description: 'Basic FastAPI endpoint',
      language: 'python',
      code: `@app.get("/api/endpoint")
async def get_endpoint():
    try:
        # Route logic here
        return {"success": True, "data": {}}
    except Exception as e:
        raise HTTPException(status_code=500, detail=str(e))`
    },
    {
      id: 'fastapi-post',
      name: 'FastAPI POST Route',
      description: 'POST endpoint with Pydantic model',
      language: 'python',
      code: `from pydantic import BaseModel

class Item(BaseModel):
    name: str
    description: str = None
    price: float

@app.post("/api/items")
async def create_item(item: Item):
    # Process item
    return {"success": True, "item": item}`
    },
    {
      id: 'fastapi-dependency',
      name: 'FastAPI Dependency',
      description: 'Dependency injection',
      language: 'python',
      code: `from fastapi import Depends

async def get_current_user(token: str = Depends(oauth2_scheme)):
    # Validate token and return user
    return user

@app.get("/api/me")
async def read_users_me(current_user = Depends(get_current_user)):
    return current_user`
    }
  ],

  mongodb: [
    {
      id: 'mongo-find',
      name: 'MongoDB Find',
      description: 'Find documents',
      language: 'javascript',
      code: `const documents = await db.collection('collectionName').find({
  // Query filter
}).toArray();`
    },
    {
      id: 'mongo-insert',
      name: 'MongoDB Insert',
      description: 'Insert document',
      language: 'javascript',
      code: `const result = await db.collection('collectionName').insertOne({
  // Document data
  createdAt: new Date()
});`
    },
    {
      id: 'mongo-update',
      name: 'MongoDB Update',
      description: 'Update document',
      language: 'javascript',
      code: `const result = await db.collection('collectionName').updateOne(
  { _id: documentId },
  {
    $set: {
      // Fields to update
      updatedAt: new Date()
    }
  }
);`
    },
    {
      id: 'mongo-delete',
      name: 'MongoDB Delete',
      description: 'Delete document',
      language: 'javascript',
      code: `const result = await db.collection('collectionName').deleteOne({
  _id: documentId
});`
    }
  ],

  tailwind: [
    {
      id: 'tw-card',
      name: 'Tailwind Card',
      description: 'Card component',
      language: 'html',
      code: `<div class="bg-white rounded-lg shadow-md p-6">
  <h2 class="text-2xl font-bold mb-4">Card Title</h2>
  <p class="text-gray-600">Card content goes here.</p>
</div>`
    },
    {
      id: 'tw-button',
      name: 'Tailwind Button',
      description: 'Button styles',
      language: 'html',
      code: `<button class="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded transition-colors">
  Click Me
</button>`
    },
    {
      id: 'tw-form',
      name: 'Tailwind Form',
      description: 'Form with inputs',
      language: 'html',
      code: `<form class="space-y-4">
  <div>
    <label class="block text-sm font-medium text-gray-700 mb-1">
      Label
    </label>
    <input
      type="text"
      class="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
      placeholder="Enter text"
    />
  </div>
  <button type="submit" class="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
    Submit
  </button>
</form>`
    },
    {
      id: 'tw-grid',
      name: 'Tailwind Grid',
      description: 'Responsive grid layout',
      language: 'html',
      code: `<div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
  <div class="bg-white p-6 rounded-lg shadow">Item 1</div>
  <div class="bg-white p-6 rounded-lg shadow">Item 2</div>
  <div class="bg-white p-6 rounded-lg shadow">Item 3</div>
</div>`
    }
  ],

  testing: [
    {
      id: 'jest-test',
      name: 'Jest Test',
      description: 'Basic Jest test',
      language: 'javascript',
      code: `describe('Component/Function', () => {
  test('should do something', () => {
    // Arrange
    const input = 'test';

    // Act
    const result = functionToTest(input);

    // Assert
    expect(result).toBe('expected');
  });
});`
    },
    {
      id: 'react-testing',
      name: 'React Component Test',
      description: 'Test React component',
      language: 'javascript',
      code: `import { render, screen, fireEvent } from '@testing-library/react';
import Component from './Component';

test('renders component', () => {
  render(<Component />);
  const element = screen.getByText(/text/i);
  expect(element).toBeInTheDocument();
});

test('handles click event', () => {
  render(<Component />);
  const button = screen.getByRole('button');
  fireEvent.click(button);
  // Assert expected behavior
});`
    }
  ],

  async: [
    {
      id: 'async-await',
      name: 'Async/Await Function',
      description: 'Async function with error handling',
      language: 'javascript',
      code: `const asyncFunction = async () => {
  try {
    const result = await someAsyncOperation();
    return result;
  } catch (error) {
    console.error('Error:', error);
    throw error;
  } finally {
    // Cleanup code
  }
};`
    },
    {
      id: 'promise-all',
      name: 'Promise.all',
      description: 'Run multiple promises in parallel',
      language: 'javascript',
      code: `const results = await Promise.all([
  promise1(),
  promise2(),
  promise3()
]);

const [result1, result2, result3] = results;`
    }
  ]
};

// Get all snippets as flat array
export const getAllSnippets = () => {
  return Object.entries(snippets).flatMap(([category, items]) =>
    items.map(item => ({ ...item, category }))
  );
};

// Search snippets
export const searchSnippets = (query) => {
  const allSnippets = getAllSnippets();
  const lowerQuery = query.toLowerCase();

  return allSnippets.filter(snippet =>
    snippet.name.toLowerCase().includes(lowerQuery) ||
    snippet.description.toLowerCase().includes(lowerQuery) ||
    snippet.id.toLowerCase().includes(lowerQuery) ||
    snippet.category.toLowerCase().includes(lowerQuery)
  );
};

// Get snippets by category
export const getSnippetsByCategory = (category) => {
  return snippets[category] || [];
};

// Get snippet by ID
export const getSnippetById = (id) => {
  const allSnippets = getAllSnippets();
  return allSnippets.find(snippet => snippet.id === id);
};
